import { useEffect, useState } from 'react';
import type { PumpSide } from '@shared/constants.ts';
import { Icon } from '../Icon.tsx';
import { SheetShell, SegRow, FieldRow } from './SheetShell.tsx';
import { fmtTime } from '../../lib/format.ts';

export function PumpStartSheet({
  open,
  onClose,
  defaultSide = 'both',
  mode = 'start',
  onSubmit,
}: {
  open: boolean;
  onClose: () => void;
  defaultSide?: PumpSide;
  mode?: 'start' | 'adjust';
  onSubmit: (vars: { side: PumpSide }) => Promise<unknown> | void;
}) {
  const [side, setSide] = useState<PumpSide>(defaultSide);

  // Re-sync local state when the sheet opens or the upstream side changes
  // (e.g. user opens Adjust while a session is in progress and we want to
  // show the session's current side, not the first-render default).
  useEffect(() => {
    if (!open) return;
    setSide(defaultSide);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, defaultSide]);

  return (
    <SheetShell
      open={open}
      onClose={onClose}
      title={mode === 'adjust' ? 'Adjust pump' : 'Pump session'}
      tagline={mode === 'adjust' ? 'Update the side without ending' : 'Start a pumping session'}
      accent="pump"
      icon={(p) => <Icon.pump {...p} />}
      primaryLabel={mode === 'adjust' ? 'Save' : 'Start pump'}
      onPrimary={async () => {
        await onSubmit({ side });
        onClose();
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <SegRow
          label="Side"
          active={side}
          onChange={setSide}
          options={[
            { id: 'left', label: 'Left only' },
            { id: 'right', label: 'Right only' },
            { id: 'both', label: 'Both' },
          ]}
        />
        <FieldRow label="Start" value={fmtTime(Math.floor(Date.now() / 1000))} hint="now" />
        <div
          style={{
            padding: 12,
            borderRadius: 12,
            background: 'var(--warn-soft)',
            border: '1px solid color-mix(in oklab, var(--warn) 30%, transparent)',
            display: 'flex',
            gap: 10,
            alignItems: 'flex-start',
          }}
        >
          <div style={{ color: 'var(--warn)' }}>
            <Icon.bell s={16} />
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-2)', lineHeight: 1.4 }}>
            We'll nudge after <span style={{ fontWeight: 600, color: 'var(--text-1)' }}>45m</span>. Adjustable in settings.
          </div>
        </div>
      </div>
    </SheetShell>
  );
}

export function PumpYieldSheet({
  open,
  onClose,
  defaultLeft = 0,
  defaultRight = 0,
  side = 'both',
  onSubmit,
  onSkip,
}: {
  open: boolean;
  onClose: () => void;
  defaultLeft?: number;
  defaultRight?: number;
  side?: PumpSide;
  onSubmit: (vars: {
    yield_left_ml: number;
    yield_right_ml: number;
    yield_ml: number;
  }) => Promise<unknown> | void;
  onSkip?: () => Promise<unknown> | void;
}) {
  const [left, setLeft] = useState(side === 'right' ? 0 : defaultLeft);
  const [right, setRight] = useState(side === 'left' ? 0 : defaultRight);

  // Re-sync local state when the sheet opens or the side changes so the
  // disabled side always reads 0 and any prior input doesn't leak through.
  useEffect(() => {
    if (!open) return;
    setLeft(side === 'right' ? 0 : defaultLeft);
    setRight(side === 'left' ? 0 : defaultRight);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, side]);

  // Zero out the inactive side on submit so the DB never stores a phantom
  // yield against a side that wasn't pumped.
  const effLeft = side === 'right' ? 0 : left;
  const effRight = side === 'left' ? 0 : right;
  const total = effLeft + effRight;

  return (
    <SheetShell
      open={open}
      onClose={onClose}
      title="Pump yield"
      tagline={
        side === 'left' ? 'Left side only' : side === 'right' ? 'Right side only' : 'Both sides'
      }
      accent="pump"
      icon={(p) => <Icon.pump {...p} />}
      primaryLabel="End pump"
      onPrimary={async () => {
        await onSubmit({
          yield_left_ml: effLeft,
          yield_right_ml: effRight,
          yield_ml: total,
        });
        onClose();
      }}
      secondaryLabel={onSkip ? 'End without yield' : 'Cancel'}
    >
      <div
        style={{
          padding: '14px 18px',
          borderRadius: 16,
          background: 'var(--bg-card)',
          border: '1px solid var(--border)',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div
            style={{
              fontSize: 11,
              fontWeight: 600,
              color: 'var(--text-3)',
              textTransform: 'uppercase',
              letterSpacing: 0.04,
            }}
          >
            Yield
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-3)' }}>
            ml ·{' '}
            {side === 'left' ? 'left only' : side === 'right' ? 'right only' : 'split L / R'}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 12, marginTop: 10 }}>
          {side !== 'right' && (
            <YieldCell label="LEFT" value={left} onChange={setLeft} color="breast" />
          )}
          {side !== 'left' && (
            <YieldCell label="RIGHT" value={right} onChange={setRight} color="breast" />
          )}
          <div
            style={{
              flex: 1,
              padding: '10px 12px',
              borderRadius: 12,
              background: 'color-mix(in oklab, var(--pump) 15%, transparent)',
            }}
          >
            <div style={{ fontSize: 10, color: 'var(--pump)', fontWeight: 600 }}>TOTAL</div>
            <div className="lt-serif lt-tabular" style={{ fontSize: 22, color: 'var(--pump)' }}>
              {total} <span style={{ fontSize: 12, color: 'var(--text-3)' }}>ml</span>
            </div>
          </div>
        </div>
      </div>
      {onSkip && (
        <button
          type="button"
          className="lt-btn lt-btn-ghost"
          style={{ width: '100%', padding: '11px 0', marginTop: 12, fontSize: 13 }}
          onClick={async () => {
            await onSkip();
            onClose();
          }}
        >
          End without entering yield
        </button>
      )}
    </SheetShell>
  );
}

function YieldCell({
  label,
  value,
  onChange,
  color,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  color: string;
}) {
  return (
    <label
      style={{
        flex: 1,
        padding: '10px 12px',
        borderRadius: 12,
        background: 'var(--bg-sunken)',
        cursor: 'text',
      }}
    >
      <div style={{ fontSize: 10, color: 'var(--text-3)' }}>{label}</div>
      <input
        inputMode="numeric"
        pattern="[0-9]*"
        value={String(value)}
        onChange={(e) => {
          const v = Number(e.target.value.replace(/[^0-9]/g, ''));
          onChange(Number.isFinite(v) ? Math.min(2000, Math.max(0, v)) : 0);
        }}
        className="lt-serif lt-tabular"
        style={{
          width: '100%',
          fontSize: 22,
          color: `var(--${color})`,
          marginTop: 2,
          padding: 0,
        }}
      />
    </label>
  );
}
